<template>
    <div>
        <div class="text-center mb-4">
            <b-button size="lg" variant="secondary" v-show="checked.length" v-b-modal.bulk-update-modal>Bulk Update ({{ checked.length }})</b-button>
        </div>

        <b-button variant="default" class="mb-4" v-on:click="selectAll()">Select / unselect all</b-button>

        <b-table striped hover :items="results" :fields="resultsFields" :current-page="currentPage" :per-page="250">
            <template slot="actions" scope="row">
                <div v-show="!isDeleted(row.item)">
                    <b-btn variant="primary" size="sm" @click.stop="edit(row.item)">Edit</b-btn>
                    <b-btn variant="danger" size="sm" @click.stop="del(row.item)">Delete</b-btn>
                </div>
                <b-btn variant="danger" disabled v-show="isDeleted(row.item)">Deleted</b-btn>
            </template>

            <template slot="check" scope="row">
                <input type="checkbox" v-model="checked" :value="row.item" />
            </template>

            <template slot="colour" scope="row">
                {{ row.item.colour && row.item.colour.label ? row.item.colour.label : '' }}
            </template>

            <template slot="ukSpecialPrice" scope="row">
                <span v-if="row.item.internationalPricing && row.item.internationalPricing.UK">
                    &pound;{{ formatPrice(row.item.internationalPricing.UK) }}
                </span>
                <span v-if="!(row.item.internationalPricing && row.item.internationalPricing.UK)">
                    n/a
                </span>
            </template>

            <template slot="ssp" scope="row">
                &pound;{{ formatPrice(row.item.ssp) }}
            </template>

            <template slot="costPrice" scope="row">
                &pound;{{ formatPrice(row.item.costPrice) }}
            </template>

            <template slot="rawPrice" scope="row">
                £{{ formatPrice(row.item.ssp) }}
            </template>

            <template slot="code" scope="row">
                {{ row.item.code }}
            </template>

            <template slot="lastModified" scope="row">
                {{ formatLastModified(row.item.lastModified) }}
            </template>
        </b-table>

        <simplert :useRadius="true" :useIcon="true" ref="simplert"></simplert>

        <b-modal id="bulk-update-modal" title="Bulk Update" size="lg" @shown="bulkUpdateShown" @hidden="resetBulkUpdate" hide-footer>
            <div v-show="!bulkUpdateInProgress && !bulkUpdateComplete">
                <p>Which attribute would you like to update?</p>

                <b-form-select v-model="bulkUpdateAttribute" :options="bulkUpdateAttributes"></b-form-select>

                <hr />

                <div id="bulk-update-colour" v-if="bulkUpdateAttribute === 'colour'">
                    <b-form-select v-model="bulkUpdateValue" :options="getColourOptions()"></b-form-select>
                </div>

                <div id="bulk-update-roof" v-if="bulkUpdateAttribute === 'roof'">
                    <b-form-select v-model="bulkUpdateValue" :options="getRoofOptions()"></b-form-select>
                </div>

                <div id="bulk-update-range" v-if="bulkUpdateAttribute === 'range'">
                    <b-form-select v-model="bulkUpdateValue" :options="getRangeOptions()"></b-form-select>
                </div>

                <div id="bulk-update-price" v-if="bulkUpdateAttribute === 'costPrice' || bulkUpdateAttribute === 'ssp' || bulkUpdateAttribute === 'internationalPricing.UK'">
                    <b-row>
                        <b-col cols="3">
                            <b-form-select v-model="bulkUpdateMethod" :options="['+ (add to)','- (subtract from)','= (set to)']"></b-form-select>
                        </b-col>

                        <b-col cols="6">
                            <b-form-input v-model="bulkUpdateValue"></b-form-input>
                        </b-col>

                        <b-col cols="3">
                            <b-form-select v-model="bulkUpdateUnit" :options="['exactly', 'percent']" v-show="bulkUpdateMethod!=='= (set to)'"></b-form-select>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12">
                            <label>
                                <input type="checkbox" v-model="bulkUpdateRound" /> Round to nearest £5?
                            </label>
                        </b-col>
                  </b-row>
                </div>

                <div id="bulk-update-optional-extras" v-if="bulkUpdateAttribute === 'optionalExtras'">
                    <b-form-select v-model="bulkUpdateUnit" :options="extraLabels" @change.native="updateBulkUpdateExtra"></b-form-select>

                    <div class="card mt-4">
                        <div class="card-body">
                            <product-extra :extra="bulkUpdateValue" v-if="bulkUpdateValue"></product-extra>
                        </div>
                    </div>
                </div>

                <div id="bulk-update-size-label" v-if="bulkUpdateAttribute === 'sizeLabel' || bulkUpdateAttribute === 'variation' || (bulkUpdateAttribute && (bulkUpdateAttribute.substring(0, 6) === 'height' || bulkUpdateAttribute.substring(0, 5) === 'width' || bulkUpdateAttribute.substring(0, 5) === 'depth' || bulkUpdateAttribute.substring(0, 4) === 'area'))">
                    <b-form-input v-model="bulkUpdateValue" placeholder="Enter the new value here"></b-form-input>
                </div>

                <div id="bulk-update-variation" v-if="bulkUpdateAttribute === 'description' || bulkUpdateAttribute === 'specification'">
                    <b-form-textarea v-model="bulkUpdateValue" rows="4" placeholder="Enter the new value here"></b-form-textarea>
                </div>

                <div id="bulk-update-family" v-if="bulkUpdateAttribute === '_family.name'">
                    <b-form-select v-model="bulkUpdateValue" :options="productFamilyLabels"></b-form-select>
                </div>

                <div class="text-center m-4" v-show="bulkUpdateAttribute && bulkUpdateValue">
                    <b-button @click="processBulkUpdate">Preview</b-button>
                </div>

                <b-table striped hover :items="checked" :fields="getCheckedFields()" v-if="bulkUpdateProcessed">
                    <template slot="check" scope="row">
                        <b-form-checkbox v-model="checked" v-on:change="update()" :value="row.item"></b-form-checkbox>
                    </template>

                    <template slot="colour" scope="row">
                        {{ row.item.colour && row.item.colour.label ? row.item.colour.label : '' }}
                    </template>

                    <template slot="roof" scope="row">
                        {{ row.item.roof && row.item.roof.label ? row.item.roof.label : '' }}
                    </template>

                    <template slot="ssp" scope="row">
                        &pound;{{ formatPrice(row.item.ssp) }}
                    </template>

                    <template slot="oldValue" scope="row">
                        <span v-if="row.item.oldValue">
                            {{ row.item.oldValue && row.item.oldValue.label ? row.item.oldValue.label : row.item.oldValue }}
                        </span>
                        <span v-if="!row.item.oldValue">
                            n/a
                        </span>
                    </template>

                    <template slot="newValue" scope="row">
                        <span v-if="row.item.newValue">
                            {{ row.item.newValue && row.item.newValue.label ? row.item.newValue.label : (row.item.newValue.name ? row.item.newValue.name : row.item.newValue) }}
                        </span>
                        <span v-if="!row.item.newValue">
                            n/a
                        </span>
                    </template>
                </b-table>
            </div>

            <div v-if="bulkUpdateProcessed">
                <div class="text-center" v-show="!bulkUpdateInProgress && !bulkUpdateComplete">
                    <b-button @click="commitBulkUpdate" size="lg" variant="warning">Commit Changes</b-button>
                </div>

                <div class="mt-4" v-show="bulkUpdateInProgress || bulkUpdateComplete">
                    <div class="mb-4" v-show="!bulkUpdateComplete">
                        <pulse-loader :loading="bulkUpdateInProgress" color="#01a94e"></pulse-loader>
                    </div>

                    <b-progress :value="bulkUpdateCommitCounter" :max="checked.length" show-progress></b-progress>
                </div>

                <p v-show="bulkUpdateComplete" class="text-center mt-4">{{ bulkUpdateCommitCounter }} products updated. You can now close this window.</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
  import Simplert from 'vue2-simplert'
  import { fb } from '../firebase'
  import ProductExtra from './ProductExtra.vue'
  import { PulseLoader } from 'vue-spinner/dist/vue-spinner'
  import {ServerValue} from 'firebase/database'

  export default {
    name: 'productList',
    props: ['results', 'fb'],
    components: {
      Simplert,
      ProductExtra,
      PulseLoader
    },
    firebase () {
      return {
        colours: {
          source: this.fb.database().ref('colours').orderByChild('name'),
          asObject: true
        },
        roofs: {
          source: this.fb.database().ref('roofs').orderByChild('name'),
          asObject: true
        },
        extras: {
          source: fb.database().ref('extras')
        },
        families: {
          source: this.fb.database().ref('families').orderByChild('name')
        },
        productTypes: {
          source: this.fb.database().ref('productTypes').orderByChild('name'),
          asObject: true
        }
      }
    },
    data () {
      return {
        resultsFields: {
          check: {
            label: ' '
          },
          code: {
            label: 'Product Code',
            sortable: true
          },
          name: {
            label: 'Name',
            sortable: true
          },
          range: {
            label: 'Product Range',
            sortable: true
          },
          colour: {
            key: 'colour',
            label: 'Colour',
            sortable: true
          },
          costPrice: {
            label: 'Cost Price',
            sortable: true
          },
          rawPrice: {
            label: 'Raw Price (exc. vat)',
            sortable: true
          },
          ukSpecialPrice: {
            label: 'UK Consumer Price (inc. vat)',
            sortable: true
          },
          lastModified: {
            label: 'Last Modified',
            sortable: true
          },
          actions: {
            label: ''
          }
        },
        deleted: [],
        checked: [],
        bulkUpdateAttributes: [
          {
            value: 'colour',
            text: 'Colour'
          },
          {
            value: 'roof',
            text: 'Roof'
          },
          {
            value: 'costPrice',
            text: 'Cost Price'
          },
          {
            value: 'ssp',
            text: 'Raw Product Price (exc. vat)'
          },
          {
            value: 'sizeLabel',
            text: 'Size Label'
          },
          {
            value: '_family.name',
            text: 'Family'
          },
          {
            value: 'range',
            text: 'Range'
          },
          {
            value: 'variation',
            text: 'Variation'
          },
          {
            value: 'internationalPricing.UK',
            text: 'UK Consumer Price (inc. vat)'
          },
          {
            value: 'description',
            text: 'Description'
          },
          {
            value: 'specification',
            text: 'Specification'
          },
          {
            value: 'areaImperial',
            text: 'Area (Imperial)'
          },
          {
            value: 'areaMetric',
            text: 'Area (Metric)'
          },
          {
            value: 'widthImperial',
            text: 'Width (Imperial)'
          },
          {
            value: 'widthMetric',
            text: 'Width (Metric)'
          },
          {
            value: 'heightImperial',
            text: 'Height (Imperial)'
          },
          {
            value: 'heightMetric',
            text: 'Height (Metric)'
          },
          {
            value: 'depthImperial',
            text: 'Depth (Imperial)'
          },
          {
            value: 'depthMetric',
            text: 'Depth (Metric)'
          }
        ],
        bulkUpdateAttribute: null,
        bulkUpdateValue: null,
        bulkUpdateProcessed: false,
        bulkUpdateMethod: '+ (add to)',
        bulkUpdateUnit: 'exactly',
        bulkUpdateCommitCounter: 0,
        bulkUpdateInProgress: false,
        bulkUpdateComplete: false,
        bulkUpdateRound: false,
        currentPage: 1
      }
    },
    computed: {
      extraLabels () {
        return this.extras.map((val) => {
          return {
            value: val.label,
            text: val.label
          }
        })
      },
      productFamilyLabels () {
        return this.families.map((val) => {
          return {
            value: val,
            text: val.name
          }
        })
      }
    },
    methods: {
      update () {
        console.log('updated')
      },
      selectAll () {
        if (this.checked.length) {
          this.checked = []
        } else {
          this.checked = []
          for (let i = 0; i < this.results.length; i++) {
            this.checked.push(this.results[i])
          }
        }
        this.$forceUpdate()
      },
      updateBulkUpdateExtra: function (e) {
        // create a new extra object
        this.bulkUpdateValue = {
          _new: true,
          extra: e.target.value,
          costPrice: null,
          maxQty: 1,
          availableTo: 'all',
          pricing: {},
          pricingInSSP: {}
        }
        this.$forceUpdate()
      },
      processBulkUpdate () {
        for (let i = 0; i < this.checked.length; i++) {
          if (!this.checked[i]._old) {
            this.checked[i]._old = JSON.parse(JSON.stringify(this.checked[i]))
          }
          let newValue
          if (this.bulkUpdateAttribute.indexOf('.') !== -1) {
            let desc = this.bulkUpdateAttribute.split('.')
            this.checked[i]['oldValue'] = this.checked[i]._old[desc[0]][desc[1]]
            newValue = this.transformValue(this.checked[i]['_old'][desc[0]][desc[1]], this.checked[i])
            this.checked[i][desc[0]][desc[1]] = newValue
          } else {
            this.checked[i]['oldValue'] = this.checked[i]._old[this.bulkUpdateAttribute]
            newValue = this.transformValue(this.checked[i]['_old'][this.bulkUpdateAttribute], this.checked[i])
            this.checked[i][this.bulkUpdateAttribute] = newValue
          }
          this.checked[i]['newValue'] = newValue
        }
        console.log(this.checked)
        this.bulkUpdateProcessed = true
        this.$forceUpdate()
      },
      commitBulkUpdate () {
        this.bulkUpdateInProgress = true
        function doUpdate (self, product) {
          return function () {
            if (product['_old']) {
              delete product['_old']
            }
            if (product.oldValue) {
              delete product.oldValue
            }
            if (product.newValue) {
              delete product.newValue
            }
            if (product['.key']) {
              delete product['.key']
            }
            console.log('updating product', product)
            let newProductCode = self.$productCode.generate(product)
            // start by updating local references
            for (let j = 0; j < self.results.length; j++) {
              if (self.results[j]['code'] === product['code']) {
                self.results[j] = product
              }
            }
            product.lastModified = ServerValue.TIMESTAMP

            if (product['code'] !== newProductCode) {
              fb.database().ref('products').child(product['code']).remove().then(() => {
                product['code'] = newProductCode
                fb.database().ref('products').child(newProductCode).set(product).then(() => {
                  self.bulkUpdateCommitCounter++
                  if (self.bulkUpdateCommitCounter === self.checked.length) {
                    self.bulkUpdateInProgress = false
                    self.bulkUpdateComplete = true
                  }
                })
              })
            } else {
              console.log('key and code match, updating current product')
              // now update db reference
              fb.database().ref('products').child(product.code).set(product).then(() => {
                self.bulkUpdateCommitCounter++
                if (self.bulkUpdateCommitCounter === self.checked.length) {
                  self.bulkUpdateInProgress = false
                  self.bulkUpdateComplete = true
                }
              })
            }
          }
        }
        for (let i = 0; i < this.checked.length; i++) {
          console.log('iteration', this.checked[i])
          setTimeout(doUpdate(this, this.checked[i]), i * 500)
        }
      },
      bulkUpdateShown () {
        window.fb = fb
        this.bulkUpdateAttribute = null
        this.bulkUpdateValue = null
        this.bulkUpdateProcessed = false
        this.bulkUpdateMethod = '+'
        this.bulkUpdateUnit = 'exactly'
      },
      transformValue (value, product) {
        if (this.bulkUpdateAttribute === 'costPrice' || this.bulkUpdateAttribute === 'ssp' || this.bulkUpdateAttribute === 'internationalPricing.UK') {
          value = parseFloat(value)
          let newValue = parseFloat(this.bulkUpdateValue)
          if (this.bulkUpdateMethod === '+ (add to)' && this.bulkUpdateUnit === 'exactly') {
            value += newValue
          } else if (this.bulkUpdateMethod === '- (subtract from)' && this.bulkUpdateUnit === 'exactly') {
            value -= newValue
          } else if (this.bulkUpdateMethod === '+ (add to)' && this.bulkUpdateUnit === 'percent') {
            value = value + ((value / 100) * newValue)
          } else if (this.bulkUpdateMethod === '- (subtract from)' && this.bulkUpdateUnit === 'percent') {
            value = value - ((value / 100) * newValue)
          } else if (this.bulkUpdateMethod === '= (set to)') {
            value = newValue
          }
          if (this.bulkUpdateRound) {
            value = Math.ceil(value / 5) * 5
          }
          return value
        } else if (this.bulkUpdateAttribute === '_family.name') {
          product.name = [this.bulkUpdateValue.name, product.variation].join(' ')
          product.family = this.bulkUpdateValue['.key']

          if (!product._family) {
            product._family = {}
          }

          product._family.images = this.bulkUpdateValue.images
          product._family.prefix = this.bulkUpdateValue.prefix
          return this.bulkUpdateValue.name
        } else {
          return this.bulkUpdateValue
        }
      },
      getCheckedFields () {
        let fields = {}
        fields['code'] = {
          label: 'Product Code'
        }
        fields['oldValue'] = {
          label: 'Old Value'
        }
        fields['newValue'] = {
          label: 'New Value'
        }
        return fields
      },
      getRangeOptions () {
        let productTypes = []
        for (let key in this.productTypes) {
          if (this.productTypes.hasOwnProperty(key) && key !== '.key') {
            productTypes.push({
              value: this.productTypes[key],
              text: this.productTypes[key]
            })
          }
        }
        return productTypes
      },
      getRoofOptions () {
        let roofs = []
        for (let key in this.roofs) {
          if (this.roofs.hasOwnProperty(key)) {
            if (this.roofs[key].name) {
              roofs.push({
                value: {
                  prefix: this.roofs[key].prefix,
                  label: this.roofs[key].name
                },
                text: this.roofs[key].name
              })
            }
          }
        }
        return roofs
      },
      getColourOptions () {
        let colours = []
        for (let key in this.colours) {
          if (this.colours.hasOwnProperty(key)) {
            if (this.colours[key].name) {
              colours.push({
                value: {
                  prefix: this.colours[key].prefix,
                  label: this.colours[key].name
                },
                text: this.colours[key].name
              })
            }
          }
        }
        return colours
      },
      isDeleted (item) {
        for (let i = 0; i < this.deleted.length; i++) {
          if (this.deleted[i] === item.code) {
            return true
          }
        }
        return false
      },
      edit (item) {
        let key = item['.key']
        if (!key) {
          key = item.code
        }
        window.open(this.$router.resolve('/product/' + key).href, '_blank')
      },
      formatPrice (value) {
        let val = (value / 1).toFixed(2)
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      },
      formatLastModified (value) {
        if (value) {
          const date = new Date(value)
          return [date.toLocaleDateString(), date.toLocaleTimeString()].join(' ')
        }
      },
      del (item) {
        let self = this
        this.$refs.simplert.openSimplert({
          title: 'Are you sure?',
          type: 'warning',
          message: 'Are you sure you want to delete this product?',
          useConfirmBtn: true,
          onConfirm: () => {
            fb.database().ref('products/' + item['.key']).remove().then(() => {
              self.deleted.push(item.code)
            })
          }
        })
      },
      resetBulkUpdate () {
        this.checked = []
        this.bulkUpdateAttribute = null
        this.bulkUpdateValue = null
        this.bulkUpdateProcessed = false
        this.bulkUpdateMethod = '+ (add to)'
        this.bulkUpdateUnit = 'exactly'
        this.bulkUpdateCommitCounter = 0
        this.bulkUpdateInProgress = false
        this.bulkUpdateComplete = false
      }
    }
  }
</script>

<style type="text/css">
@media screen and (min-width: 1600px) {
    .modal-lg {
        max-width: 800px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ProductList.vue